/**
 * The community's `<head>`: icon, title and share card.
 *
 * One `index.html` serves every domain, so what is in it is scaffolding:
 * the Memberfy icon (`/favicon.svg`), "Memberfy" for the title and — more embarrassing —
 * an `og:image` pointing at `https://bolt.new/static/og_default.png`, left over
 * from the scaffold and shown when sharing **any** community.
 *
 * Settings has always accepted a favicon, a social image, a title and a
 * description. None of it was read: the columns were filled in and ignored.
 *
 * This runs on the client, and covers the browser tab and bookmarks. It does
 * **not** cover link bots — see the note at the end of the file.
 */

import { imageUrl } from '@/lib/images'

interface CommunityHead {
  name?: string | null
  description?: string | null
  faviconUrl?: string | null
  ogImageUrl?: string | null
  metaTitle?: string | null
  metaDescription?: string | null
  metaKeywords?: string | null
  domain?: string | null
}

/** Finds the tag, or creates it — `index.html` does not carry them all. */
const tag = (selector: string, create: () => HTMLElement): HTMLElement => {
  const existing = document.head.querySelector(selector)
  if (existing) return existing as HTMLElement
  const made = create()
  document.head.appendChild(made)
  return made
}

const meta = (attr: 'name' | 'property', key: string) =>
  tag(`meta[${attr}="${key}"]`, () => {
    const el = document.createElement('meta')
    el.setAttribute(attr, key)
    return el
  })

const setMeta = (attr: 'name' | 'property', key: string, value: string | null) => {
  if (!value) {
    document.head.querySelector(`meta[${attr}="${key}"]`)?.remove()
    return
  }
  meta(attr, key).setAttribute('content', value)
}

/**
 * Swaps the tab icon.
 *
 * Removes the existing `<link rel="icon">` elements rather than reusing one:
 * the served HTML may declare an SVG, and a PNG favicon inheriting
 * `type="image/svg+xml"` is rejected by some browsers — it disappears with no
 * error at all.
 *
 * The `?v=` is what makes the swap show up. A favicon is among the things a
 * browser caches hardest, and without a new URL the tab keeps the old icon
 * until somebody clears the cache.
 */
const applyFavicon = (url: string | null | undefined) => {
  document.head.querySelectorAll('link[rel~="icon"]').forEach((el) => el.remove())

  const link = document.createElement('link')
  link.setAttribute('rel', 'icon')

  if (!url) {
    /* No favicon of its own, so the product's. Leaving none makes the browser
       ask for `/favicon.ico`, take a 404 and show the blank sheet. */
    link.setAttribute('type', 'image/svg+xml')
    link.setAttribute('href', '/favicon.svg?v=2')
    document.head.appendChild(link)
    return
  }

  const extension = url.split('?')[0].split('.').pop()?.toLowerCase()
  const type = extension === 'ico'
    ? 'image/x-icon'
    : extension === 'webp'
      ? 'image/webp'
      : extension === 'svg'
        ? 'image/svg+xml'
        : 'image/png'

  link.setAttribute('type', type)
  /* `.ico` does not go through imgix — it is the one format here it will not
     convert. The others go at 64px, which covers the tab on a retina screen. */
  const source = extension === 'ico' ? url : (imageUrl(url, { w: 64, h: 64, fit: 'crop' }) || url)
  link.setAttribute('href', `${source}${source.includes('?') ? '&' : '?'}v=${encodeURIComponent(url.slice(-12))}`)
  document.head.appendChild(link)
}

/**
 * Who owns the title on this route.
 *
 * `server.js` writes the route it resolved into a `<meta name="memberfy:ssr">`.
 * While the app is still on that route the title is the server's: it knew the
 * call, and the client on boot knows only the community and the space —
 * rewriting swaps the specific for the generic.
 *
 * On the first navigation ownership moves to the client and does not move back.
 * Under `npm run dev` there is no mark at all, so the client owns it from the
 * start, the way it always did.
 */
let clientOwnsTitle = false

export const serverOwnsTitleFor = (path: string): boolean => {
  if (clientOwnsTitle) return false
  const rendered = document.head
    .querySelector('meta[name="memberfy:ssr"]')
    ?.getAttribute('content')
  if (rendered !== null && rendered !== undefined && rendered === path) return true
  clientOwnsTitle = true
  return false
}

/** The community's primary address (`https://<domain>`), kept for the route changes. `undefined` until the community loads: the server's tags stay. */
let primaryOrigin: string | null | undefined

/**
 * `<link rel="canonical">` and `og:url` on the primary address, for the page
 * being shown. The server writes both into the HTML; this keeps them right
 * after a client-side navigation, and on a page opened from the other address.
 */
export const syncCanonical = (path: string) => {
  if (primaryOrigin === undefined) return
  const existing = document.head.querySelector('link[rel="canonical"]')
  if (!primaryOrigin) {
    existing?.remove()
    setMeta('property', 'og:url', null)
    return
  }
  const url = `${primaryOrigin}${path}`
  const link = (existing as HTMLLinkElement | null) || (() => {
    const made = document.createElement('link')
    made.rel = 'canonical'
    document.head.appendChild(made)
    return made
  })()
  link.href = url
  setMeta('property', 'og:url', url)
}

/**
 * Applies everything. Called once the community loads, and whenever it changes.
 *
 * `null` returns to the scaffold, which is the path for somebody opening a
 * domain with no community: the error screen must not inherit the previous
 * community's brand.
 *
 * `keepTitle` leaves `document.title`, the description and the `og:` title and
 * description alone. The server already wrote better ones for this route, and
 * overwriting the title on boot is what made the tab flash.
 */
export const applyCommunityHead = (
  community: CommunityHead | null,
  { keepTitle = false }: { keepTitle?: boolean } = {}
) => {
  if (!community) {
    applyFavicon(null)
    if (!keepTitle) document.title = 'Memberfy'
    for (const [attr, key] of [
      ['property', 'og:title'], ['property', 'og:description'], ['property', 'og:image'],
      ['property', 'og:url'], ['property', 'og:site_name'],
      ['name', 'description'], ['name', 'keywords'], ['name', 'twitter:image']
    ] as const) setMeta(attr, key, null)
    primaryOrigin = null
    syncCanonical(window.location.pathname)
    return
  }

  const title = community.metaTitle || community.name || 'Memberfy'
  const description = community.metaDescription || community.description || null

  applyFavicon(community.faviconUrl)
  if (!keepTitle) document.title = title

  /* `keepTitle`: the server wrote this route's head, title and description
     alike (the space's or the call's, and the community's language fallback
     when its description is empty or the "{name} community" placeholder).
     Rewriting them on boot is what a crawler running JavaScript read. */
  if (!keepTitle) {
    setMeta('name', 'description', description)
    setMeta('property', 'og:title', title)
    setMeta('property', 'og:description', description)
  }
  setMeta('name', 'keywords', community.metaKeywords || null)
  setMeta('property', 'og:site_name', community.name || null)
  /* F-146: `domain` is the primary address (the active custom domain, else the
     Memberfy subdomain), so both addresses point search engines at one. */
  primaryOrigin = community.domain ? `https://${community.domain}` : null
  syncCanonical(window.location.pathname)

  /* 1200×630 is what the networks expect; larger is wasted bandwidth and
     smaller gets cropped. No image of its own means none — a card with no image
     beats another product's placeholder, which is what used to be here. */
  const social = community.ogImageUrl
    ? imageUrl(community.ogImageUrl, { w: 1200, h: 630, fit: 'crop' }) || community.ogImageUrl
    : null
  setMeta('property', 'og:image', social)
  setMeta('name', 'twitter:image', social)
  setMeta('name', 'twitter:card', social ? 'summary_large_image' : 'summary')
}

/*
 * ⚠️ This fixes the browser tab, not the link preview.
 *
 * WhatsApp, Slack, LinkedIn and the search engines read the raw HTML without
 * running JavaScript, so nothing here reaches them. The preview is decided by
 * `server.js`, which writes the same tags into the HTML before sending it — and
 * goes further, because it knows the route and can speak about the call, not
 * just the community.
 *
 * They are two halves of one thing: the server covers whoever reads the HTML
 * and stops there; this covers navigation once the app takes over.
 */
